import type { StreamConfig } from '../types/stream';

// Decides *whether* a downloaded-and-waiting update may be applied right now,
// based on the stream's update_preference. This only gates timing - the actual
// reload is always performed at a content boundary (or while idle) by the
// player, so an update never cuts an item mid-playback.
//
// Supported update_preference values (case-insensitive):
//   'immediate' / 'asap' -> apply at the next content boundary.
//   'midnight'           -> apply during the local 00:00 minute.
//   'HH:mm'              -> apply during that local minute.
//   '' / unknown         -> apply at reboot_time when auto_reboot is set,
//                           otherwise at the next content boundary.
export function shouldApplyUpdate(config: StreamConfig | null, now: Date = new Date()): boolean {
  if (!config) return false;
  const pref = (config.update_preference || '').trim().toLowerCase();

  if (pref === 'immediate' || pref === 'asap') return true;

  const target = pref === 'midnight' ? '00:00' : parseHhmm(pref);
  if (target) return currentHhmm(now) === target;

  if (config.auto_reboot && config.reboot_time) {
    return currentHhmm(now) === config.reboot_time;
  }

  return true;
}

function parseHhmm(value: string): string | null {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(value) ? value : null;
}

function currentHhmm(d: Date): string {
  const pad = (n: number) => (n < 10 ? `0${n}` : String(n));
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
